import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders overlays (modals, dialogs) on <body>, confined to the content area.
 *
 * Why a portal: the app's content column is its own stacking context (App.tsx:
 * `position: relative; zIndex: 0`), so a `position: fixed` overlay rendered inside it is
 * drawn below the sidebars whatever its own z-index.
 *
 * Why a region: the left sidebar (z 1200) and ACV's navbar (drawn by the host over the
 * iframe) can't be covered from inside the iframe without veiling ACV. So the region is the
 * content area — right of the sidebar, below ACV's navbar — and `transform` makes it the
 * containing block for fixed descendants: an overlay's `position: fixed; inset: 0` fills
 * the content area instead of the whole window. Sizes come from CSS variables set by
 * main.tsx (ACV layout info) and App.tsx (sidebar width).
 *
 * The region ignores clicks itself (it can stay mounted with nothing open); overlays inside
 * it take clicks normally.
 */
const regionStyle: React.CSSProperties = {
  position: 'fixed',
  top: 'var(--acv-embed-top-offset, 0px)',
  left: 'calc(var(--acv-vertical-navbar-width, 0px) + var(--airec-sidebar-width, 0px))',
  right: 0,
  bottom: 0,
  transform: 'translateZ(0)',
  zIndex: 1100, // above the right sidebar (999); the mobile sidebar drawer (1400) still wins
  pointerEvents: 'none',
};

const BodyPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  typeof document === 'undefined'
    ? null
    : createPortal(
        <div className="airec-overlay-region" style={regionStyle}>
          {children}
        </div>,
        document.body,
      );

export default BodyPortal;
